import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Filter, X, CheckCircle } from 'lucide-react';
import { getAgendamentosParaCalendario, getAgendadores, getClosers, getCurrentCloser } from '../services/api';
import { logError } from '../lib/logError';
import { ghlOppUrl } from '../lib/ghlLink';

// ─── Constants ────────────────────────────────────────────────────────────────

const SDR_COLORS = [
    '#00dc82', '#00A3FF', '#8B5CF6', '#F59E0B',
    '#EF4444', '#f97b4f', '#f94f8a', '#4ff9f0',
    '#c3f26e', '#a78bfa',
];

const MONTH_NAMES = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const DAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const DAY_FULL  = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
const TIPOS     = ['Novo Agendamento', 'Reagendamento', 'Agendamento de Transferido'];
const HOURS     = Array.from({ length: 15 }, (_, i) => i + 7); // 07–21
const SLOT_H    = 60; // px per 1h slot
const TIME_W    = 52; // px for time label column

// ─── Helpers ─────────────────────────────────────────────────────────────────

const toDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const formatBR = (s) => {
    if (!s) return '—';
    const [y, m, d] = s.split('-');
    return `${d}/${m}/${y}`;
};

const getWeekStart = (date) => {
    const d = new Date(date);
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    return d;
};

const getWeekDates = (start) =>
    Array.from({ length: 7 }, (_, i) => {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        return d;
    });

const hourOf = (timeStr) => (timeStr ? parseInt(timeStr.split(':')[0], 10) : null);

// ─── Main Component ───────────────────────────────────────────────────────────

const CalendarioAgendamentos = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const [view,       setView]       = useState('month');
    const [viewYear,   setViewYear]   = useState(now.getFullYear());
    const [viewMonth,  setViewMonth]  = useState(now.getMonth());
    const [weekStart,  setWeekStart]  = useState(() => getWeekStart(now));
    const [dayDate,    setDayDate]    = useState(new Date(now));

    const [events,               setEvents]               = useState([]);
    const [loading,              setLoading]              = useState(false);
    const [selected,             setSelected]             = useState(null);
    const [filterSDRs,           setFilterSDRs]           = useState([]);
    const [filterTipos,          setFilterTipos]          = useState([]);
    const [filterClosers,        setFilterClosers]        = useState([]);
    const [allSDRs,              setAllSDRs]              = useState([]);
    const [allClosers,           setAllClosers]           = useState([]);
    const [showFilters,          setShowFilters]          = useState(false);
    const [currentCloser,        setCurrentCloser]        = useState(null);
    const [filterMinhasReunioes, setFilterMinhasReunioes] = useState(false);
    const [currentTime,          setCurrentTime]          = useState(() => {
        const t = new Date();
        return { hour: t.getHours(), minutes: t.getMinutes() };
    });

    // Load SDR list, closer list and current closer once
    useEffect(() => {
        getAgendadores()
            .then(data =>
                setAllSDRs(
                    data.filter(p => p.cargo?.trim().toLowerCase() !== 'social seller')
                        .map(p => p.nome)
                        .sort()
                )
            )
            .catch(() => {});
        getClosers()
            .then(data => setAllClosers(data.map(p => p.nome).sort()))
            .catch(() => {});
        getCurrentCloser()
            .then(closer => {
                setCurrentCloser(closer);
                if (closer) setFilterMinhasReunioes(true);
            })
            .catch(() => {});
    }, []);

    // Atualiza linha de horário atual a cada minuto
    useEffect(() => {
        const interval = setInterval(() => {
            const t = new Date();
            setCurrentTime({ hour: t.getHours(), minutes: t.getMinutes() });
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    // Fetch events for visible range
    const fetchRange = useCallback(async (start, end) => {
        setLoading(true);
        try {
            setEvents(await getAgendamentosParaCalendario(toDateStr(start), toDateStr(end)));
        } catch (err) {
            logError(err, { component: 'CalendarioAgendamentos' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (view === 'month') {
            const s = new Date(viewYear, viewMonth, 1);
            s.setDate(s.getDate() - 7);
            fetchRange(s, new Date(viewYear, viewMonth + 1, 7));
        } else if (view === 'week') {
            const e = new Date(weekStart);
            e.setDate(e.getDate() + 6);
            fetchRange(weekStart, e);
        } else {
            fetchRange(dayDate, dayDate);
        }
    }, [view, viewYear, viewMonth, weekStart, dayDate, fetchRange]);

    // Color map
    const sdrColor = useMemo(() => {
        const m = {};
        allSDRs.forEach((s, i) => { m[s] = SDR_COLORS[i % SDR_COLORS.length]; });
        return m;
    }, [allSDRs]);

    // Filtered + grouped by date
    const filtered = useMemo(() =>
        events.filter(ev => {
            if (filterSDRs.length    && !filterSDRs.includes(ev.agendador))          return false;
            if (filterTipos.length   && !filterTipos.includes(ev.tipo_agendamento))  return false;
            if (filterClosers.length && !filterClosers.includes(ev.closer_assigned)) return false;
            if (filterMinhasReunioes && currentCloser && ev.closer_assigned !== currentCloser.nome) return false;
            return true;
        }),
    [events, filterSDRs, filterTipos, filterClosers, filterMinhasReunioes, currentCloser]);

    const byDate = useMemo(() => {
        const m = {};
        filtered.forEach(ev => {
            if (!m[ev.data_reuniao]) m[ev.data_reuniao] = [];
            m[ev.data_reuniao].push(ev);
        });
        return m;
    }, [filtered]);

    // Navigation
    const goToday = () => {
        const t = new Date(now);
        setViewYear(t.getFullYear()); setViewMonth(t.getMonth());
        setWeekStart(getWeekStart(t)); setDayDate(new Date(t));
    };
    const prev = () => {
        if (view === 'month') {
            if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
            else setViewMonth(m => m - 1);
        } else if (view === 'week') {
            setWeekStart(w => { const d = new Date(w); d.setDate(d.getDate() - 7); return d; });
        } else {
            setDayDate(d => { const n = new Date(d); n.setDate(n.getDate() - 1); return n; });
        }
    };
    const next = () => {
        if (view === 'month') {
            if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
            else setViewMonth(m => m + 1);
        } else if (view === 'week') {
            setWeekStart(w => { const d = new Date(w); d.setDate(d.getDate() + 7); return d; });
        } else {
            setDayDate(d => { const n = new Date(d); n.setDate(n.getDate() + 1); return n; });
        }
    };

    const headerLabel = useMemo(() => {
        if (view === 'month') return `${MONTH_NAMES[viewMonth]} ${viewYear}`;
        if (view === 'week') {
            const dates = getWeekDates(weekStart);
            const s = dates[0], e = dates[6];
            if (s.getMonth() === e.getMonth())
                return `${s.getDate()}–${e.getDate()} de ${MONTH_NAMES[s.getMonth()]} ${s.getFullYear()}`;
            return `${s.getDate()} ${MONTH_NAMES[s.getMonth()].slice(0, 3)} – ${e.getDate()} ${MONTH_NAMES[e.getMonth()].slice(0, 3)} ${e.getFullYear()}`;
        }
        return `${DAY_FULL[dayDate.getDay()]}, ${dayDate.getDate()} de ${MONTH_NAMES[dayDate.getMonth()]} ${dayDate.getFullYear()}`;
    }, [view, viewYear, viewMonth, weekStart, dayDate]);

    const todayStr     = toDateStr(now);
    const filterCount  = filterSDRs.length + filterTipos.length + filterClosers.length + (filterMinhasReunioes ? 1 : 0);
    const toggleChip   = (list, set, v) => set(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v]);

    // Month grid
    const monthDays = useMemo(() => {
        const first    = new Date(viewYear, viewMonth, 1);
        const startDow = first.getDay();
        const total    = new Date(viewYear, viewMonth + 1, 0).getDate();
        const days     = [];
        for (let i = 0; i < startDow; i++)
            days.push({ date: new Date(viewYear, viewMonth, 1 - (startDow - i)), cur: false });
        for (let i = 1; i <= total; i++)
            days.push({ date: new Date(viewYear, viewMonth, i), cur: true });
        for (let i = 1; days.length < 42; i++)
            days.push({ date: new Date(viewYear, viewMonth + 1, i), cur: false });
        return days;
    }, [viewYear, viewMonth]);

    return (
        <>
        <div className="page-header">
            <div className="page-header-left">
                <div>
                    <div className="page-kicker">Agenda</div>
                    <h1 className="page-title">Calendário de Agendamentos</h1>
                </div>
            </div>
        </div>

        <div className="page-body">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            {/* ── Toolbar ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button onClick={prev} style={S.navBtn}><ChevronLeft size={18} /></button>
                    <span style={{ minWidth: '220px', textAlign: 'center', fontWeight: 600, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                        {headerLabel}
                    </span>
                    <button onClick={next} style={S.navBtn}><ChevronRight size={18} /></button>
                    <button onClick={goToday} style={{ ...S.navBtn, padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}>Hoje</button>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {/* View switcher */}
                    <div style={{ display: 'flex', border: '1px solid var(--border-color)', borderRadius: '6px', overflow: 'hidden' }}>
                        {[['month','Mês'], ['week','Semana'], ['day','Dia']].map(([v, label], i) => (
                            <button key={v} onClick={() => setView(v)} style={{
                                background: view === v ? 'rgba(0,220,130,0.15)' : 'transparent',
                                border: 'none',
                                borderLeft: i > 0 ? '1px solid var(--border-color)' : 'none',
                                color: view === v ? 'var(--primary-color)' : 'var(--text-secondary)',
                                padding: '0.3rem 0.8rem',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: view === v ? 600 : 400,
                            }}>
                                {label}
                            </button>
                        ))}
                    </div>

                    <button onClick={() => setShowFilters(f => !f)} style={{ ...S.navBtn, display: 'flex', alignItems: 'center', gap: '0.4rem', position: 'relative', padding: '0.35rem 0.75rem' }}>
                        <Filter size={15} /> Filtros
                        {filterCount > 0 && (
                            <span style={{ position: 'absolute', top: '-5px', right: '-5px', background: 'var(--primary-color)', borderRadius: '50%', width: '15px', height: '15px', fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#000', fontWeight: 700 }}>
                                {filterCount}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* ── Filter panel ── */}
            {showFilters && (
                <div style={{ background: 'var(--panel-bg)', borderRadius: '8px', padding: '1rem', border: '1px solid var(--border-color)' }}>
                    <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                        <div>
                            <div style={S.filterLabel}>Agendador</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                                {allSDRs.map(sdr => (
                                    <button key={sdr} onClick={() => toggleChip(filterSDRs, setFilterSDRs, sdr)} style={chip(filterSDRs.includes(sdr), sdrColor[sdr])}>
                                        <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: sdrColor[sdr], marginRight: '5px' }} />
                                        {sdr}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div style={S.filterLabel}>Tipo</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                                {TIPOS.map(t => (
                                    <button key={t} onClick={() => toggleChip(filterTipos, setFilterTipos, t)} style={chip(filterTipos.includes(t), 'var(--primary-color)')}>
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {allClosers.length > 0 && (
                            <div>
                                <div style={S.filterLabel}>Closer</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                                    {allClosers.map(c => (
                                        <button key={c} onClick={() => toggleChip(filterClosers, setFilterClosers, c)} style={chip(filterClosers.includes(c), '#8B5CF6')}>
                                            {c}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    {currentCloser && (
                        <div>
                            <div style={S.filterLabel}>Minhas Reuniões</div>
                            <button
                                onClick={() => setFilterMinhasReunioes(v => !v)}
                                style={chip(filterMinhasReunioes, 'var(--primary-color)')}
                            >
                                {currentCloser.nome}
                            </button>
                        </div>
                    )}
                    {filterCount > 0 && (
                        <button onClick={() => { setFilterSDRs([]); setFilterTipos([]); setFilterClosers([]); setFilterMinhasReunioes(false); }} style={{ marginTop: '0.75rem', background: 'none', border: 'none', color: 'var(--primary-color)', cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}>
                            Limpar filtros
                        </button>
                    )}
                </div>
            )}

            {/* ── SDR legend ── */}
            {allSDRs.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                    {allSDRs.map(sdr => (
                        <div key={sdr} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: sdrColor[sdr], flexShrink: 0 }} />
                            {sdr}
                        </div>
                    ))}
                </div>
            )}

            {/* ── View content ── */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-secondary)' }}>Carregando...</div>
            ) : view === 'month' ? (
                <MonthView days={monthDays} byDate={byDate} sdrColor={sdrColor} todayStr={todayStr} onEvent={setSelected} />
            ) : view === 'week' ? (
                <WeekView
                    weekStart={weekStart} byDate={byDate} sdrColor={sdrColor} todayStr={todayStr}
                    onEvent={setSelected}
                    onDayClick={d => { setDayDate(d); setView('day'); }}
                    currentTime={currentTime}
                />
            ) : (
                <DayView date={dayDate} events={byDate[toDateStr(dayDate)] || []} sdrColor={sdrColor} todayStr={todayStr} onEvent={setSelected} currentTime={currentTime} />
            )}

            {/* ── Detail modal (read-only; ações de comparecimento/closer são feitas no GHL) ── */}
            {selected && (
                <div onClick={() => { setSelected(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div onClick={e => e.stopPropagation()} style={{ background: 'var(--orbion-surface, #04091c)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '400px', position: 'relative' }}>
                        <button onClick={() => { setSelected(null); }} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex' }}>
                            <X size={18} />
                        </button>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: sdrColor[selected.agendador] || '#00dc82', flexShrink: 0 }} />
                            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>{selected.nome}</h3>
                            {selected.tipo_reuniao && (
                                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', color: 'var(--primary-color)', fontWeight: 600 }}>
                                    <CheckCircle size={14} /> Concluída
                                </span>
                            )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                            <DR label="Data"      value={formatBR(selected.data_reuniao)} />
                            {selected.horario_reuniao && <DR label="Horário"   value={selected.horario_reuniao.slice(0, 5)} />}
                            <DR label="Agendador" value={selected.agendador} />
                            <DR label="Tipo"      value={selected.tipo_agendamento} />
                            {selected.closer_assigned && <DR label="Closer" value={selected.closer_assigned} />}
                            {selected.numero_reuniao && <DR label="Rodada" value={selected.numero_reuniao} />}
                            {selected.tipo_reuniao && <DR label="Reunião" value={selected.tipo_reuniao} />}
                            {selected.estrelas > 0 && <DR label="Estrelas" value={'★'.repeat(selected.estrelas)} />}
                            <DR label="Comparecimento" value={selected.comparecimento === true ? 'Compareceu' : selected.comparecimento === false ? 'Faltou' : 'Pendente'} />
                        </div>
                        {ghlOppUrl(selected.ghl_opportunity_id) && (
                            <div style={{ marginTop: '1.25rem', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '1rem' }}>
                                <a
                                    href={ghlOppUrl(selected.ghl_opportunity_id)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="pill pill-primary"
                                    style={{ width: '100%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', textDecoration: 'none' }}
                                >
                                    Abrir card no GHL ↗
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
        </div>
        </>
    );
};

// ─── Month View ───────────────────────────────────────────────────────────────

const MonthView = ({ days, byDate, sdrColor, todayStr, onEvent }) => (
    <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: 'rgba(255,255,255,0.03)' }}>
            {DAY_SHORT.map(d => (
                <div key={d} style={{ padding: '0.5rem 0', textAlign: 'center', fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {d}
                </div>
            ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {days.map(({ date, cur }, idx) => {
                const key   = toDateStr(date);
                const evts  = byDate[key] || [];
                const today = key === todayStr;
                return (
                    <div key={idx} style={{ minHeight: '90px', minWidth: 0, padding: '0.35rem', borderTop: '1px solid var(--border-color)', borderLeft: idx % 7 !== 0 ? '1px solid var(--border-color)' : 'none', background: today ? 'rgba(0,220,130,0.04)' : 'transparent' }}>
                        <div style={{ width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', fontSize: '0.78rem', fontWeight: today ? 700 : 400, color: today ? '#000' : cur ? 'var(--text-primary)' : 'rgba(255,255,255,0.2)', background: today ? 'var(--primary-color)' : 'transparent', marginBottom: '0.3rem' }}>
                            {date.getDate()}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {evts.slice(0, 3).map(ev => (
                                <button key={ev.id} onClick={() => onEvent(ev)} title={`${ev.nome} — ${ev.agendador}`} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '2px 5px', borderRadius: '3px', border: 'none', background: (sdrColor[ev.agendador] || '#00dc82') + 'cc', color: '#000', fontSize: '0.67rem', fontWeight: 600, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: '1.5' }}>
                                    {ev.horario_reuniao ? ev.horario_reuniao.slice(0, 5) + ' · ' : ''}{ev.nome}
                                </button>
                            ))}
                            {evts.length > 3 && (
                                <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', paddingLeft: '3px' }}>+{evts.length - 3} mais</div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    </div>
);

// ─── Week View ────────────────────────────────────────────────────────────────

const WeekView = ({ weekStart, byDate, sdrColor, todayStr, onEvent, onDayClick, currentTime }) => {
    const dates = getWeekDates(weekStart);

    // Events without time — one per day
    const hasUntimed = dates.some(d => (byDate[toDateStr(d)] || []).some(ev => !ev.horario_reuniao));

    return (
        <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>

            {/* Day header row */}
            <div style={{ display: 'grid', gridTemplateColumns: `${TIME_W}px repeat(7, 1fr)`, borderBottom: '1px solid var(--border-color)', background: 'rgba(255,255,255,0.03)', position: 'sticky', top: 0, zIndex: 2 }}>
                <div style={{ borderRight: '1px solid var(--border-color)' }} />
                {dates.map((date, i) => {
                    const today = toDateStr(date) === todayStr;
                    return (
                        <div key={i} onClick={() => onDayClick(date)} style={{ padding: '0.5rem 0.25rem', textAlign: 'center', borderLeft: i > 0 ? '1px solid var(--border-color)' : 'none', cursor: 'pointer', background: today ? 'rgba(0,220,130,0.06)' : 'transparent' }}>
                            <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {DAY_SHORT[date.getDay()]}
                            </div>
                            <div style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', margin: '0.15rem auto 0', fontSize: '0.9rem', fontWeight: today ? 700 : 500, color: today ? '#000' : 'var(--text-primary)', background: today ? 'var(--primary-color)' : 'transparent' }}>
                                {date.getDate()}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Untimed events row */}
            {hasUntimed && (
                <div style={{ display: 'grid', gridTemplateColumns: `${TIME_W}px repeat(7, 1fr)`, borderBottom: '1px solid var(--border-color)', background: 'rgba(255,255,255,0.015)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '8px', fontSize: '0.62rem', color: 'var(--text-secondary)', borderRight: '1px solid var(--border-color)', padding: '4px 8px 4px 4px' }}>
                        s/ hora
                    </div>
                    {dates.map((date, i) => {
                        const evts = (byDate[toDateStr(date)] || []).filter(ev => !ev.horario_reuniao);
                        return (
                            <div key={i} style={{ minWidth: 0, borderLeft: i > 0 ? '1px solid rgba(255,255,255,0.05)' : 'none', padding: '3px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                {evts.map(ev => (
                                    <button key={ev.id} onClick={() => onEvent(ev)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '2px 5px', borderRadius: '3px', border: 'none', background: (sdrColor[ev.agendador] || '#00dc82') + 'cc', color: '#000', fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {ev.nome}
                                    </button>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Time grid */}
            <div style={{ overflowY: 'auto', maxHeight: '580px' }}>
                {HOURS.map(hour => (
                    <div key={hour} style={{ display: 'grid', gridTemplateColumns: `${TIME_W}px repeat(7, 1fr)`, height: `${SLOT_H}px`, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        {/* Time label */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', paddingRight: '8px', paddingTop: '5px', fontSize: '0.68rem', color: 'var(--text-secondary)', borderRight: '1px solid var(--border-color)', flexShrink: 0 }}>
                            {String(hour).padStart(2, '0')}:00
                        </div>
                        {/* Day columns */}
                        {dates.map((date, di) => {
                            const ds   = toDateStr(date);
                            const evts = (byDate[ds] || []).filter(ev => hourOf(ev.horario_reuniao) === hour);
                            const today = ds === todayStr;
                            const showLine = today && currentTime && currentTime.hour === hour;
                            const lineTop  = showLine ? (currentTime.minutes / 60) * SLOT_H : null;
                            return (
                                <div key={di} style={{ position: 'relative', minWidth: 0, borderLeft: di > 0 ? '1px solid rgba(255,255,255,0.04)' : 'none', background: today ? 'rgba(0,220,130,0.02)' : 'transparent', padding: '2px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    {showLine && (
                                        <div style={{ position: 'absolute', top: lineTop, left: 0, right: 0, height: '2px', background: '#ef4444', zIndex: 5, pointerEvents: 'none' }}>
                                            <div style={{ position: 'absolute', left: -4, top: -3, width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />
                                        </div>
                                    )}
                                    {evts.map(ev => (
                                        <button
                                            key={ev.id}
                                            onClick={() => onEvent(ev)}
                                            title={`${ev.nome} — ${ev.agendador}`}
                                            style={{
                                                flex: 1,
                                                minHeight: 0,
                                                width: '100%',
                                                minWidth: 0,
                                                boxSizing: 'border-box',
                                                textAlign: 'left',
                                                padding: '3px 5px',
                                                borderRadius: '4px',
                                                border: 'none',
                                                background: (sdrColor[ev.agendador] || '#00dc82') + 'dd',
                                                color: '#000',
                                                fontSize: '0.68rem',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                overflow: 'hidden',
                                                lineHeight: '1.3',
                                            }}
                                        >
                                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {ev.horario_reuniao.slice(0, 5)} {ev.nome}
                                            </div>
                                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.7, fontSize: '0.62rem' }}>
                                                {ev.agendador}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─── Day View ─────────────────────────────────────────────────────────────────

const DayView = ({ date, events, sdrColor, todayStr, onEvent, currentTime }) => {
    const ds       = toDateStr(date);
    const today    = ds === todayStr;
    const timed    = events.filter(ev => ev.horario_reuniao);
    const untimed  = events.filter(ev => !ev.horario_reuniao);

    return (
        <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-color)', background: 'rgba(255,255,255,0.03)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: today ? 'var(--primary-color)' : 'rgba(255,255,255,0.08)', color: today ? '#000' : 'var(--text-primary)', fontWeight: 700, fontSize: '1.1rem', flexShrink: 0 }}>
                    {date.getDate()}
                </div>
                <div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{DAY_FULL[date.getDay()]}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{MONTH_NAMES[date.getMonth()]} {date.getFullYear()}</div>
                </div>
                <div style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {events.length} evento{events.length !== 1 ? 's' : ''}
                </div>
            </div>

            {/* Untimed events */}
            {untimed.length > 0 && (
                <div style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', background: 'rgba(255,255,255,0.015)' }}>
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>Sem horário:</span>
                    {untimed.map(ev => (
                        <button key={ev.id} onClick={() => onEvent(ev)} style={{ padding: '2px 8px', borderRadius: '3px', border: 'none', background: (sdrColor[ev.agendador] || '#00dc82') + 'cc', color: '#000', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' }}>
                            {ev.nome}
                        </button>
                    ))}
                </div>
            )}

            {/* Time slots */}
            <div style={{ overflowY: 'auto', maxHeight: '600px' }}>
                {HOURS.map(hour => {
                    const slotEvts = timed.filter(ev => hourOf(ev.horario_reuniao) === hour);
                    const showLine = ds === todayStr && currentTime && currentTime.hour === hour;
                    const lineTop  = showLine ? (currentTime.minutes / 60) * SLOT_H : null;
                    return (
                        <div key={hour} style={{ display: 'flex', minHeight: `${SLOT_H}px`, borderBottom: '1px solid rgba(255,255,255,0.04)', background: slotEvts.length ? 'rgba(0,220,130,0.02)' : 'transparent' }}>
                            <div style={{ width: `${TIME_W}px`, flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', paddingRight: '10px', paddingTop: '6px', fontSize: '0.72rem', color: 'var(--text-secondary)', borderRight: '1px solid var(--border-color)' }}>
                                {String(hour).padStart(2, '0')}:00
                            </div>
                            <div style={{ position: 'relative', flex: 1, padding: '4px 8px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignContent: 'flex-start' }}>
                                {showLine && (
                                    <div style={{ position: 'absolute', top: lineTop, left: 0, right: 0, height: '2px', background: '#ef4444', zIndex: 5, pointerEvents: 'none' }}>
                                        <div style={{ position: 'absolute', left: -4, top: -3, width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />
                                    </div>
                                )}
                                {slotEvts.map(ev => (
                                    <button key={ev.id} onClick={() => onEvent(ev)} style={{ textAlign: 'left', padding: '6px 12px', borderRadius: '6px', border: 'none', background: (sdrColor[ev.agendador] || '#00dc82') + 'dd', color: '#000', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', minWidth: '180px', maxWidth: '320px' }}>
                                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {ev.horario_reuniao.slice(0, 5)} · {ev.nome}
                                        </div>
                                        <div style={{ fontSize: '0.7rem', opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {ev.agendador} · {ev.tipo_agendamento}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ─── Shared ───────────────────────────────────────────────────────────────────

const DR = ({ label, value }) => (
    <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.875rem' }}>
        <span style={{ color: 'var(--text-secondary)', minWidth: '80px', flexShrink: 0 }}>{label}:</span>
        <span style={{ color: 'var(--text-primary)' }}>{value || '—'}</span>
    </div>
);

const S = {
    navBtn: {
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid var(--border-color)',
        borderRadius: '6px',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        padding: '0.35rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    filterLabel: {
        fontSize: '0.72rem',
        color: 'var(--text-secondary)',
        marginBottom: '0.5rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
    },
};

const chip = (active, color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.28rem 0.65rem',
    borderRadius: '20px',
    border: `1px solid ${active ? color : 'var(--border-color)'}`,
    background: active ? (color.startsWith('#') ? color + '22' : 'rgba(0,220,130,0.13)') : 'transparent',
    color: active ? color : 'var(--text-secondary)',
    cursor: 'pointer',
    fontSize: '0.78rem',
    fontWeight: active ? 600 : 400,
    transition: 'all 0.15s',
});

export default CalendarioAgendamentos;
